import { useState, useMemo } from 'react';
import styled from 'styled-components';
import { REVIEW_TAGS, REVIEW_COMMENT_MAX, createReview } from '../../../services/reviewService';

/**
 * 고객 리뷰 작성 모달
 * props:
 *   serviceRequest: {id, category, assigned_partner_id, partnerName, customer_id, customerName, sourceRealtorId}
 *   onClose(): 닫기
 *   onDone(reviewId): 작성 완료 콜백
 */
const RatingModal = ({ serviceRequest, onClose, onDone }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [selectedTags, setSelectedTags] = useState([]);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = useMemo(() => rating > 0 && !submitting, [rating, submitting]);

  const toggleTag = (key) => {
    setSelectedTags((prev) => prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key]);
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      setError('별점을 선택해주세요.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const id = await createReview({
        serviceRequestId: serviceRequest.id,
        customerId: serviceRequest.customer_id || serviceRequest.customerId,
        customerName: serviceRequest.customerName || '',
        partnerId: serviceRequest.assigned_partner_id || serviceRequest.partnerId,
        partnerName: serviceRequest.partnerName || '',
        sourceRealtorId: serviceRequest.sourceRealtorId || serviceRequest.source_realtor_id,
        category: serviceRequest.category,
        rating,
        tags: selectedTags,
        comment,
      });
      onDone?.(id);
    } catch (err) {
      console.error('리뷰 작성 실패:', err);
      setError('리뷰 등록에 실패했습니다. 잠시 후 다시 시도해주세요.');
      setSubmitting(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Sheet onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>서비스는 어떠셨나요?</Title>
          <Sub>경험을 공유해주시면 더 나은 서비스를 제공할 수 있어요.</Sub>
        </Header>

        {/* 별점 */}
        <Section>
          <StarRow>
            {[1, 2, 3, 4, 5].map((n) => (
              <StarBtn
                key={n}
                type="button"
                onClick={() => setRating(n)}
                onMouseEnter={() => setHoverRating(n)}
                onMouseLeave={() => setHoverRating(0)}
              >
                <StarIcon filled={n <= (hoverRating || rating)}>★</StarIcon>
              </StarBtn>
            ))}
          </StarRow>
          <RatingLabel>
            {rating === 0 ? '별점을 선택해주세요' :
             rating <= 2 ? '많이 아쉬우셨군요' :
             rating === 3 ? '괜찮으셨나요?' :
             rating === 4 ? '만족스러우셨네요' : '정말 만족하셨군요!'}
          </RatingLabel>
        </Section>

        {/* 태그 */}
        <Section>
          <Label>어떤 점이 좋으셨나요?</Label>
          <TagList>
            {REVIEW_TAGS.map((t) => (
              <TagChip
                key={t.key}
                type="button"
                $selected={selectedTags.includes(t.key)}
                onClick={() => toggleTag(t.key)}
              >
                {t.label}
              </TagChip>
            ))}
          </TagList>
        </Section>

        {/* 코멘트 */}
        <Section>
          <LabelRow>
            <Label>한줄 후기</Label>
            <CharCount>{comment.length} / {REVIEW_COMMENT_MAX}</CharCount>
          </LabelRow>
          <TextArea
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, REVIEW_COMMENT_MAX))}
            placeholder="자세한 후기를 남겨주세요 (선택)"
          />
        </Section>

        {error && <ErrorText>{error}</ErrorText>}

        <ButtonRow>
          <GhostBtn type="button" onClick={onClose} disabled={submitting}>나중에 할게요</GhostBtn>
          <PrimaryBtn type="button" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? '등록 중...' : '리뷰 등록'}
          </PrimaryBtn>
        </ButtonRow>
      </Sheet>
    </Overlay>
  );
};

export default RatingModal;

// --- Styled ---
const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex; align-items: flex-end; justify-content: center;
  z-index: 1000;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 20px 20px 0 0;
  padding: 28px 22px 24px;
  animation: slideUp 0.25s ease-out;
  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
`;

const Header = styled.div`margin-bottom: 20px;`;
const Title = styled.h2`font-size: 18px; font-weight: 700; color: #191F28; margin: 0 0 6px;`;
const Sub = styled.p`font-size: 13px; color: #8B95A1; margin: 0;`;

const Section = styled.div`margin-bottom: 22px;`;
const Label = styled.div`font-size: 13px; font-weight: 600; color: #191F28; margin-bottom: 10px;`;
const LabelRow = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;`;
const CharCount = styled.span`font-size: 11px; color: #8B95A1;`;

const StarRow = styled.div`
  display: flex; justify-content: center; gap: 6px;
  margin-bottom: 10px;
`;
const StarBtn = styled.button`
  background: none; border: none; cursor: pointer; padding: 4px;
`;
const StarIcon = styled.span`
  font-size: 36px;
  color: ${({ filled }) => (filled ? '#FBBF24' : '#E5E7EB')};
  transition: color 0.15s;
`;
const RatingLabel = styled.p`
  text-align: center;
  font-size: 13px;
  color: #4B5563;
  margin: 0;
`;

const TagList = styled.div`display: flex; flex-wrap: wrap; gap: 8px;`;
const TagChip = styled.button`
  padding: 8px 14px;
  border: 1px solid ${({ $selected }) => ($selected ? '#4A6CF7' : '#E5E7EB')};
  background: ${({ $selected }) => ($selected ? '#EEF2FF' : '#fff')};
  color: ${({ $selected }) => ($selected ? '#4338CA' : '#6B7280')};
  border-radius: 18px;
  font-size: 13px;
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
  cursor: pointer;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  font-size: 14px;
  resize: vertical;
  font-family: inherit;
  &:focus { outline: none; border-color: #4A6CF7; }
`;

const ErrorText = styled.div`
  margin-bottom: 14px;
  padding: 10px 12px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 8px;
  color: #B91C1C;
  font-size: 13px;
`;

const ButtonRow = styled.div`display: flex; gap: 8px;`;
const GhostBtn = styled.button`
  flex: 1;
  height: 48px;
  border: 1px solid #E5E7EB;
  background: #fff;
  color: #6B7280;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const PrimaryBtn = styled.button`
  flex: 2;
  height: 48px;
  border: none;
  background: #4A6CF7;
  color: #fff;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:disabled { background: #CBD5E1; cursor: not-allowed; }
`;
